import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Product } from '../../services/shopService';
import { Button } from '../ui/button';
import { ShoppingCart, Eye } from 'lucide-react';
import { useToast } from '../../hooks/useToast';
import { useTenantContext } from '../../context/TenantContext';

interface ProductCardProps {
  product: Product;
}

export const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { toast } = useToast();
  const { tenant } = useTenantContext();
  
  // Trouver l'image mise en avant ou la première disponible
  const featuredImage = product.images?.find(img => img.is_featured) || product.images?.[0];
  const imageUrl = featuredImage?.image_url || '/images/placeholder-product.jpg';
  
  // Formater le prix en XAF
  const formattedPrice = product.price 
    ? new Intl.NumberFormat('fr-FR').format(product.price) + ' FCFA'
    : 'Prix sur demande';
    
  // Fonction pour ajouter au panier
  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Récupérer le panier actuel du localStorage
    const cartString = localStorage.getItem('shop_cart');
    const cart = cartString ? JSON.parse(cartString) : [];
    
    // Vérifier si le produit est déjà dans le panier
    const existingItemIndex = cart.findIndex((item: any) => item.productId === product.id);
    
    if (existingItemIndex >= 0) {
      // Mettre à jour la quantité si le produit existe déjà
      cart[existingItemIndex].quantity += 1;
    } else {
      // Ajouter le nouveau produit au panier
      cart.push({
        productId: product.id,
        title: product.title,
        price: product.price,
        currency: 'XAF',
        quantity: 1,
        imageUrl: imageUrl,
        slug: product.slug
      });
    }
    
    // Sauvegarder le panier mis à jour
    localStorage.setItem('shop_cart', JSON.stringify(cart));
    window.dispatchEvent(new Event('cart-updated'));
    
    toast({
      title: 'Produit ajouté au panier',
      description: `${product.title} a été ajouté à votre panier. Voir le panier`,
      variant: 'default'
    });
  };

  return (
    <div 
      className="group relative block bg-white/5 backdrop-blur-sm overflow-hidden rounded-lg border border-white/10 text-white transition-all duration-300 hover:shadow-2xl hover:scale-[1.02]"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        borderColor: 'rgba(255, 255, 255, 0.1)',
        '--tw-border-color': isHovered ? (tenant?.config?.color1 || '#fe8d17') : 'rgba(255, 255, 255, 0.1)'
      } as React.CSSProperties}
    >
      {/* Badge "Ressource Unique" ou "Édition Limitée" */}
      <div 
        className={`absolute top-2 right-2 z-10 px-3 py-1 text-xs font-semibold rounded-full backdrop-blur-sm border transition-all duration-300 ${isHovered ? 'opacity-100 scale-100' : 'opacity-80 scale-95'}`}
        style={{
          backgroundColor: product.is_unique 
            ? `${tenant?.config?.color1 || '#fe8d17'}e6` 
            : 'rgba(255, 255, 255, 0.2)',
          borderColor: product.is_unique 
            ? tenant?.config?.color1 || '#fe8d17' 
            : 'rgba(255, 255, 255, 0.3)',
          color: product.is_unique 
            ? tenant?.config?.color3 || '#ffffff' 
            : tenant?.config?.color3 || '#ffffff'
        }}
      >
        {product.is_unique ? 'Produit Unique' : 'Édition Limitée'}
      </div>
      
      {/* Lien vers la page détaillée */}
      <Link to={`/boutique/produit/${product.slug}`} className="block">
        {/* Image du produit */}
        <div className="aspect-square overflow-hidden">
          <img 
            src={imageUrl} 
            alt={product.title} 
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.onerror = null;
              target.src = '/images/placeholder-product.jpg';
            }}
          />
        </div>
      </Link>
      
      {/* Overlay au survol */}
      <div 
        className={`absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent transition-opacity duration-300 ${
          isHovered ? 'opacity-100' : 'opacity-0'
        }`}
        style={{
          backgroundImage: isHovered ? `linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent, transparent)` : undefined
        }}
      />
      
      {/* Boutons d'action */}
      <div className={`absolute top-1/2 left-0 right-0 flex justify-center gap-3 transform -translate-y-1/2 transition-all duration-300 ${isHovered ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}`}>
        <Button
          size="icon"
          variant="default"
          className="shadow-lg transition-all duration-200 active:scale-95 focus:ring-2"
          style={{
            backgroundColor: tenant?.config?.color1 || '#fe8d17',
            backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e07a10'})`,
            color: tenant?.config?.color3 || '#ffffff',
            '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#e07a10'}, ${tenant?.config?.color1 || '#fe8d17'})`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e07a10'})`;
          }}
          onClick={handleAddToCart}
          aria-label="Ajouter au panier"
        >
          <ShoppingCart size={18} />
        </Button>
        
        <Link to={`/boutique/produit/${product.slug}`}>
          <Button
            size="icon"
            variant="secondary"
            className="backdrop-blur-sm shadow-lg transition-all duration-200 active:scale-95 focus:ring-2 border"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              color: tenant?.config?.color3 || '#ffffff',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
            }}
            aria-label="Voir le produit"
          >
            <Eye size={18} />
          </Button>
        </Link>
      </div>
      
      {/* Informations du produit */}
      <div className="p-4 bg-white/5 backdrop-blur-sm border-t border-white/10">
        <Link to={`/boutique/produit/${product.slug}`}>
          <h3 className="text-xl font-bold leading-tight mb-2 hover:scale-[1.02] transform transition-transform duration-300"
            style={{
              color: tenant?.config?.color3 || '#ffffff',
              '--tw-text-color': isHovered ? (tenant?.config?.color1 || '#fe8d17') : (tenant?.config?.color3 || '#ffffff')
            } as React.CSSProperties}
          >
            {product.title}
          </h3>
        </Link>
        
        <div className="flex items-center justify-between mt-3">
          <p className="text-sm font-medium px-3 py-1 rounded-full backdrop-blur-sm"
            style={{
              color: tenant?.config?.color3 || '#ffffff',
              backgroundColor: `${tenant?.config?.color3 || '#fe8d17'}1a`,
              borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`
            }}
          >
            {product.category?.name || 'Informatique'}
          </p>
          <div className="px-3 py-1 rounded-full text-sm font-bold shadow-lg transform group-hover:scale-105 transition-transform duration-300"
            style={{
              backgroundColor: tenant?.config?.color1 || '#fe8d17',
              backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e07a10'})`,
              color: tenant?.config?.color3 || '#ffffff'
            }}
          >
            {formattedPrice}
          </div>
        </div>
        
        {product.stock_quantity !== undefined && (
          <div className="mt-2 text-xs" style={{ color: `${tenant?.config?.color1 || '#9ca3af'}99` }}>
            Stock: {product.stock_quantity} disponible{product.stock_quantity > 1 ? 's' : ''}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductCard;
